import { Caption1, MessageBar, MessageBarBody, Text, makeStyles } from '@fluentui/react-components';
import { z } from 'zod';
import { externalProcessPhaseSchema, externalProcessStateSchema, getStepType } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import type { StepPortalProps, StepReviewProps, StepUiModule } from '../contract.js';

const useStyles = makeStyles({
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
});

const kernel = getStepType('external-process');

/**
 * What the PACKET carries about this step: a state and a phase. An unreadable value falls back
 * to "not started", which is the honest reading of "we cannot say" — never a claim of progress.
 */
const personVisibleStatusSchema = z.object({
  state: externalProcessStateSchema,
  phase: externalProcessPhaseSchema.nullable().default(null),
});

/**
 * How each external state reads to a person. One registry, `satisfies` an exhaustive record,
 * so adding a state is a compile error naming the missing wording rather than a blank chip.
 *
 * The candidate wording is deliberately neutral and never claims anything about employment
 * authorisation. "Completed" here means *the external system says it is done*, which is not
 * the same sentence as "you are authorised to work" and must never be written as if it were.
 */
const stateWording = {
  not_started: {
    hr: 'Not started — invitation not recorded as sent',
    candidate: 'Not started yet. HR will be in touch.',
  },
  pending_external: {
    hr: 'In progress',
    candidate: 'This is in progress.',
  },
  completed_external: {
    /**
     * The HR label states the CONDITION, not just the outcome, because selecting it IS the
     * confirmation. An operator choosing "Complete" from a dropdown is asserting something
     * specific, and the words they read at that moment are the whole of the control.
     */
    hr: 'Confirmed complete — both sections done and the form is retrievable',
    candidate: 'This has been completed. There is nothing further for you to do here.',
  },
  needs_hr_attention: {
    hr: 'Needs attention',
    candidate: 'We are sorting this out. There is nothing for you to do here.',
  },
} as const satisfies Record<'not_started' | 'pending_external' | 'completed_external' | 'needs_hr_attention', { readonly hr: string; readonly candidate: string }>;

/**
 * Which half is outstanding, in each audience's words.
 *
 * The candidate wording never says "we are waiting for you to upload documents", because they
 * upload nothing here — their work happens in the external system, and telling them otherwise
 * in this portal would send them looking for a control that does not exist.
 */
const phaseWording = {
  employee: {
    hr: 'Waiting on the employee (Section 1)',
    candidate: 'Your part is outstanding. Follow the instructions HR sent you.',
  },
  hr_verification: {
    hr: 'Waiting on us (Section 2 review)',
    candidate: 'Your part is done. HR is completing their review.',
  },
} as const satisfies Record<'employee' | 'hr_verification', { readonly hr: string; readonly candidate: string }>;

/**
 * What the CANDIDATE sees — a status and an explanation, never a form.
 *
 * This type has a portal half precisely because `hr-record`'s absence of one would be the
 * wrong answer here. An obligation the candidate is responsible for, happening in another
 * system, has to be visible to them or they are left wondering why their file is incomplete.
 * What it must not do is ask them for anything: no upload control, no attestation, no
 * document fields. The step is office-submitted and the server refuses a person-submitted
 * office step, so this component has nothing to send even if it tried.
 */
function ExternalProcessPortal({ config, status }: StepPortalProps) {
  const styles = useStyles();
  const settings = kernel.configSchema.safeParse(config);
  /**
   * The packet's whitelist, NOT the stored submission — which is why this parses its own
   * two-field shape rather than the kernel's submission schema. The candidate's copy carries
   * no `confirmedBy` and no `confirmedAt`, so the full schema would reject it and every
   * checkpoint would render as "not started".
   */
  const answer = personVisibleStatusSchema.safeParse(status);
  const state = answer.success ? answer.data.state : 'not_started';
  const phase = answer.success ? answer.data.phase : null;

  return (
    <div className={styles.block}>
      <Text>{settings.success ? settings.data.candidateGuidance : 'This is completed separately from this portal. HR will provide instructions.'}</Text>
      <MessageBar intent={state === 'completed_external' ? 'success' : 'info'}>
        <MessageBarBody>
          {stateWording[state].candidate}
          {phase === null ? '' : ` ${phaseWording[phase].candidate}`}
        </MessageBarBody>
      </MessageBar>
      {/*
        * A link ONLY when a security-reviewed integration supplies a candidate-specific one.
        * A generic QuickBooks sign-up page is not that: it is a URL this system cannot vouch
        * for, pointing at a place the person may not even have an account. Absent one, we name
        * who will send the invitation and stop.
        */}
      {settings.success && settings.data.externalUrl !== null ? (
        <Text>
          <a href={settings.data.externalUrl} rel="noreferrer noopener" target="_blank">
            Open {settings.data.externalSystem === 'quickbooks-workforce' ? 'QuickBooks Workforce' : 'the external system'}
          </a>
        </Text>
      ) : (
        <Caption1>
          HR will send your QuickBooks Workforce invitation separately. Follow the
          acceptable-document instructions HR provides. You do not upload anything for this here.
        </Caption1>
      )}
    </div>
  );
}

/** What HR sees on the review card: the state, who confirmed it, and how. */
function ExternalProcessReview({ config, submission }: StepReviewProps) {
  const styles = useStyles();
  const settings = kernel.configSchema.safeParse(config);
  const answer = kernel.submissionSchema.safeParse(submission);

  return (
    <div className={styles.block}>
      <Text weight="semibold">{settings.success ? settings.data.heading : 'Completed in another system'}</Text>
      {answer.success ? (
        <>
          <Text>{stateWording[answer.data.state].hr}</Text>
          {answer.data.phase === null ? null : <Caption1>{phaseWording[answer.data.phase].hr}</Caption1>}
          {answer.data.invitationRecordedAt === null ? null : (
            <Caption1>Invitation recorded {new Date(answer.data.invitationRecordedAt).toLocaleDateString()}</Caption1>
          )}
          <Caption1>
            Recorded {new Date(answer.data.confirmedAt).toLocaleDateString()}
            {answer.data.source === null ? '' : ` · ${answer.data.source.replace(/_/g, ' ')}`}
          </Caption1>
          {answer.data.note === '' ? null : <Caption1>{answer.data.note}</Caption1>}
        </>
      ) : (
        <Text>Nothing recorded yet.</Text>
      )}
    </div>
  );
}

export const externalProcessUi: StepUiModule = {
  portal: ExternalProcessPortal as StepUiModule['portal'],
  review: ExternalProcessReview,
};
